import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Suggestion } from '@balance/domain';
import { api } from '../api.js';
import { TransactionEditor } from '../components/TransactionEditor.js';
import { Badge, ConceptName, Empty } from '../components/ui.js';
import { eur, periodLabel } from '../format.js';
import { useApp } from '../state.js';

/**
 * Alertas inductivas: pendientes de clasificación o período, recuperables vencidos, excesos y Remitly
 * incompleto. Se muestra la operación, la explicación y los destinos posibles; aplicar una sugerencia
 * es una acción explícita y queda trazada; nunca crea una regla permanente.
 */
export function AlertsPage() {
  const { balance, catalog, movements, run } = useApp();
  const [editing, setEditing] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Record<string, Suggestion>>({});
  useEffect(() => {
    api
      .suggestions()
      .then((list) => setSuggestions(Object.fromEntries(list.map((s) => [s.transactionId, s]))))
      .catch(() => undefined);
  }, [balance]);
  if (!balance || !catalog || !movements) return <div className="skeleton" />;
  const pending = balance.pending;
  const overdue = balance.receivables.filter((r) => r.overdue);
  const overs = Object.values(balance.meters).flat().filter((m) => m.overCents > 0 && m.period <= balance.currentPeriod);
  const total = pending.length + overdue.length + overs.length;
  const conceptName = (id: string | null) => catalog.concepts.find((c) => c.id === id)?.name ?? null;

  async function apply(p: (typeof pending)[number], s: Suggestion) {
    const conceptId = s.conceptId;
    if (!conceptId) return;
    const existing = movements!.allocations.filter((a) => a.transactionId === p.transactionId);
    await run(
      () =>
        api.replaceAllocations(p.transactionId, [
          ...existing.map((a) => ({ id: a.id, conceptId: a.conceptId, period: a.period, occurrenceKeys: a.occurrenceKeys, subPeriod: a.subPeriod, amountCents: a.amountCents, recoverableCents: a.recoverableCents, isMainMonthly: a.isMainMonthly, note: a.note, decidedBy: a.decidedBy })),
          { conceptId, period: s.period, subPeriod: s.subPeriod, occurrenceKeys: s.occurrenceKey ? [s.occurrenceKey] : [], amountCents: p.pendingCents, isMainMonthly: !!s.grocery, decidedBy: s.ruleId ? `rule:${s.ruleId}` : 'calendar' },
        ]),
      `Imputado a ${conceptName(conceptId)} · ${periodLabel(s.period)}`,
    );
  }

  return (
    <div className="page">
      <div className="panelhead">
        <h2>{total === 1 ? '1 decisión pendiente' : `${total} decisiones pendientes`}</h2>
        <span className="muted">Ver no equivale a resolver. Aplicar una sugerencia no crea una regla permanente.</span>
      </div>
      {total === 0 ? (
        <div className="panel">
          <Empty>Todo al día. No quedan alertas por resolver.</Empty>
        </div>
      ) : null}
      {pending.map((p) => {
        const t = movements.transactions.find((x) => x.id === p.transactionId)!;
        const s = suggestions[p.transactionId];
        const remitly = t.source === 'remitly';
        const target = s?.conceptId ? conceptName(s.conceptId) : null;
        return (
          <div className="panel" key={p.transactionId}>
            <div className="panelhead">
              <div>
                <h2>{t.description}</h2>
                <p className="muted">
                  {t.date}
                  {t.time ? ` ${t.time}` : ' · sin hora'} · {t.source} · {p.allocatedCents ? `repartido ${eur(p.allocatedCents)} de ${eur(p.amountCents)}` : 'sin clasificar'}
                </p>
                {remitly ? <Badge kind="warn">Remitly incompleto: el residual sigue pendiente y no se oculta del balance</Badge> : null}
                {t.direction === 'in' ? <Badge kind="neutral">Entrada sin destino: ingreso extraordinario, nómina prevista o devolución</Badge> : null}
                {s ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="inline">
                      {target && s.conceptId ? (
                        <Badge>
                          Sugerencia: <ConceptName id={s.conceptId} name={target} /> · {periodLabel(s.period)}
                          {s.subPeriod ? ` · ${s.calendar.kind === 'leisure' ? s.calendar.weekend.label : s.subPeriod}` : ''}
                        </Badge>
                      ) : (
                        <Badge kind="warn">Sin destino automático: elegir manualmente</Badge>
                      )}
                      {s.askPeriod ? <Badge kind="coral">confirmar período</Badge> : null}
                      {s.leisureSuppressed ? <Badge kind="neutral">no va a ocio por calendario</Badge> : null}
                      {s.grocery ? <Badge kind="warn">posible compra mensual de {periodLabel(s.grocery.targetPeriod)}</Badge> : null}
                      {s.occurrenceLabel ? <Badge>cubre {s.occurrenceLabel}</Badge> : null}
                    </div>
                    <ul className="muted" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                      {s.reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <span className="big">{eur(p.pendingCents)}</span>
            </div>
            <div className="inline">
              {s?.conceptId && !s.askPeriod ? (
                <button type="button" className="button primary" onClick={() => void apply(p, s)}>
                  Aplicar sugerencia
                </button>
              ) : null}
              <button type="button" className={`button${s?.conceptId && !s.askPeriod ? '' : ' primary'}`} onClick={() => setEditing(p.transactionId)}>
                {remitly ? 'Repartir' : 'Elegir destino y período'}
              </button>
            </div>
          </div>
        );
      })}
      {overdue.map((r) => (
        <div className="panel" key={r.receivableId}>
          <div className="panelhead">
            <div>
              <h2>Devolución vencida · {r.description}</h2>
              <p className="muted">Vencía el {r.dueOn}. Lo no recuperado ({eur(r.pendingCents)}) vuelve como costo propio al período {r.period}. El cierre es idempotente.</p>
            </div>
            <span className="big">{eur(r.pendingCents)}</span>
          </div>
          <div className="inline">
            <button type="button" className="button primary" onClick={() => void run(() => api.closeReceivable(r.receivableId), 'Pendiente cerrado: el residual vuelve al gasto original')}>
              Cerrar y asumir residual
            </button>
            <Link className="button" to="/por-recuperar">
              Registrar cobro
            </Link>
          </div>
        </div>
      ))}
      {overs.map((m) => (
        <div className="panel" key={`${m.conceptId}-${m.period}`}>
          <div className="panelhead">
            <div>
              <h2>
                <ConceptName id={m.conceptId} name={m.name} /> supera el presupuesto
              </h2>
              <p className="muted">
                {periodLabel(m.period)}: {eur(m.spentCents)} sobre {eur(m.budgetCents)}. El exceso ya impacta en la proyección; la dotación no se amplía automáticamente.
              </p>
            </div>
            <span className="big negative">{eur(m.overCents)}</span>
          </div>
          <Link className="button" to="/">
            Ver en la vista mensual
          </Link>
        </div>
      ))}
      {editing ? <TransactionEditor transactionId={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
